// Предложения в чёрный список, разобранные из выгрузки мессенджера (сценарий 112).
//
// ## Главное правило экрана: ни одна запись не попадает в список сама
//
// Готового чёрного списка заказчик не передавал — он живёт перепиской в двух группах,
// «Нарушители» и «Кандидаты». Разобрать её может только языковая модель, и она ошибается:
// путает однофамильцев, принимает за фамилию название объекта, вытаскивает чужой телефон.
// Поэтому разбор даёт ПРЕДЛОЖЕНИЯ, а классификацию делает человек — это прямое требование
// тз 5.8.2, пункт 3, и единственный путь из предложения в запись лежит через решение.
//
// Отсюда и грамматика строки: это не запись чёрного списка, а строка на другом столе. Своё
// представление, а не отбор внутри чёрного списка — у строк разный смысл, и подпись «74
// записи» над смесью из записей и предложений перестала бы что-либо означать (правило 2).

import { RiInboxUnarchiveLine, RiUploadCloud2Line } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { ListPane } from '@/layout/ListPane';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';

import type { DraftVerdict, MessengerImportOut } from '../api/blacklist';
import {
  DRAFT_FORMS,
  DRAFT_VERDICT_COLORS,
  DRAFT_VERDICT_TITLES,
  IMPORT_STATUS_TITLES,
  MESSENGER_GROUP_TITLES,
} from '../dictionaries';
import { useDraftsQuery, useImportsQuery } from '../queries';
import type { HiringRoute } from '../route';
import {
  BLANK,
  FILTER_IMPORT,
  FILTER_VERDICT,
  PANEL_NEW_IMPORT,
  hiringHref,
  openPanelHref,
  setFilter,
} from '../route';

/** Отбор из адреса в параметры запроса. Одно место перевода на весь экран. */
export function draftsQueryFromRoute(search: SearchState): {
  import_id?: string;
  verdict?: string;
} {
  return {
    import_id: search.filters[FILTER_IMPORT],
    verdict: search.filters[FILTER_VERDICT],
  };
}

export function DraftsList({
  route,
}: {
  route: HiringRoute;
}) {
  const navigate = useNavigate();
  const { search, rowId } = route;

  const params = draftsQueryFromRoute(search);
  const query = useDraftsQuery(params);
  const imports = useImportsQuery();

  function apply(next: SearchState) {
    navigate(hiringHref('drafts', next, rowId), { replace: true });
  }

  const выгрузки = imports.data?.items ?? [];
  const выбранная = выгрузки.find((выгрузка) => выгрузка.id === params.import_id);
  const narrowed = params.import_id !== undefined || params.verdict !== undefined;
  // Пока список выгрузок не приехал, утверждать «выгрузок нет» нельзя: это разные факты, и на
  // одном из них человек пойдёт заводить то, что уже заведено (правило 7).
  const выгрузокНет = imports.data !== undefined && выгрузки.length === 0;

  const filters = (
    <>
      <SelectField
        id='drafts-import'
        label='Выгрузка'
        value={params.import_id ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_IMPORT, value === NO_VALUE ? undefined : value))
        }
        options={выгрузки.map((выгрузка) => ({
          value: выгрузка.id,
          label: подписьВыгрузки(выгрузка),
        }))}
        noneLabel='Все выгрузки'
        placeholder='Все выгрузки'
        pending={imports.isPending}
        failed={imports.isError}
        failedError={imports.error as ApiError | null}
        emptyNote='Выгрузок ещё не принимали: отбирать предложения не по чему.'
        // Числа выгрузки живут в подсказке, а не в подписи пункта: в раскрытом списке они
        // превратили бы каждую строку в абзац, а нужны они только по выбранной.
        hint={выбранная === undefined ? undefined : описаниеВыгрузки(выбранная)}
      />

      <SelectField
        id='drafts-verdict'
        label='Решение'
        value={params.verdict ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_VERDICT, value === NO_VALUE ? undefined : value))
        }
        options={(Object.keys(DRAFT_VERDICT_TITLES) as DraftVerdict[]).map((code) => ({
          value: code,
          label: DRAFT_VERDICT_TITLES[code],
        }))}
        noneLabel='Любое решение'
        placeholder='Любое решение'
      />
    </>
  );

  return (
    <ListPane
      caption={query.data === undefined ? 'Предложения' : countLabel(query.data.total, DRAFT_FORMS)}
      sliceAt={parseMoment(query.data?.generated_at)}
      filters={filters}
      appliedFilters={[
        search.filters[FILTER_IMPORT] === undefined ? null : 'выгрузка',
        search.filters[FILTER_VERDICT] === undefined ? null : 'решение',
      ].filter((имя): имя is string => имя !== null)}
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='предложения из выгрузки мессенджера'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiInboxUnarchiveLine}
            title={
              выгрузокНет
                ? 'Выгрузок ещё не принимали'
                : narrowed
                  ? 'Под этот отбор предложений нет'
                  : 'Предложений пока нет'
            }
            description={
              выгрузокНет
                ? 'Готового чёрного списка у заказчика нет: он живёт перепиской в группах «Нарушители» и «Кандидаты». Примите выгрузку такой группы — модель разберёт её на предложения, а решение по каждому примете вы.'
                : narrowed
                  ? 'Список пуст из-за выбранной выгрузки или решения, а не потому, что предложений нет вовсе. Снимите отбор — и увидите остальные.'
                  : 'Выгрузки приняты, но ни одна ещё не разобрана. Выберите выгрузку в отборе выше — справа откроется её карточка с разбором.'
            }
            action={
              выгрузокНет ? (
                <LinkButton.Root variant='primary' size='medium' underline asChild>
                  <Link to={openPanelHref(route, PANEL_NEW_IMPORT, BLANK)}>Принять выгрузку</Link>
                </LinkButton.Root>
              ) : narrowed ? (
                <LinkButton.Root variant='primary' size='medium' underline asChild>
                  <Link to={hiringHref('drafts', { ...search, filters: {} })}>Снять отбор</Link>
                </LinkButton.Root>
              ) : undefined
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((draft) => (
              <ListRow
                key={draft.id}
                to={hiringHref('drafts', search, draft.id)}
                selected={draft.id === rowId}
                // Имя может не распознаться вовсе — это обычный исход разбора переписки, а не
                // сбой: в сообщении «этого с Заречной больше не берём» имени нет. Пустая
                // строка на его месте читалась бы как «не догрузилось» (правило 7).
                title={непустое(draft.full_name) ?? 'Имя не распознано'}
                subtitle={[
                  непустое(draft.phone_normalized) ?? 'телефон не распознан',
                  непустое(draft.suggested_reason_title) ?? 'причина не предложена',
                ].join(' · ')}
                badge={
                  <Badge.Root
                    variant='lighter'
                    color={DRAFT_VERDICT_COLORS[draft.verdict] ?? 'gray'}
                    size='medium'
                  >
                    {draft.verdict_title}
                  </Badge.Root>
                }
              />
            ))}

            {data.items.length < data.total && (
              <div className='px-4 py-2'>
                <Hint.Root>
                  Показаны первые {data.items.length} из {data.total}. Отберите по выгрузке или по
                  решению — иначе нужное предложение может остаться за пределами страницы.
                </Hint.Root>
              </div>
            )}

            <div className='flex justify-center p-3'>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_NEW_IMPORT, BLANK))}
              >
                <Button.Icon as={RiUploadCloud2Line} />
                Принять выгрузку
              </Button.Root>
            </div>
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}

/** Пустая строка с сервера — это отсутствие значения, а не значение из пробелов. */
function непустое(значение: string | null | undefined): string | null {
  const текст = (значение ?? '').trim();
  return текст === '' ? null : текст;
}

/** Чем выгрузка подписана в списке отбора: группа и день, когда её приняли. */
function подписьВыгрузки(выгрузка: MessengerImportOut): string {
  const группа = MESSENGER_GROUP_TITLES[выгрузка.group_kind] ?? выгрузка.group_kind;
  return `${группа}, ${formatDateOnly(выгрузка.created_at)}`;
}

/** Что известно о выбранной выгрузке: состояние и три числа, ради которых сюда и заходят. */
function описаниеВыгрузки(выгрузка: MessengerImportOut): string {
  const состояние = IMPORT_STATUS_TITLES[выгрузка.status] ?? выгрузка.status;
  return `${состояние}. Разобрано сообщений: ${выгрузка.messages_total}, предложений: ${выгрузка.drafts_total}, ждут решения: ${выгрузка.pending}.`;
}
